import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { GapActionRow, useGapResolution, sortPendingFirst, pendingCountOf } from "./gapResolution";

// Supplier ficha — two tabs like the article/material ficha: Pendientes (its gaps + inline actions)
// and Trazabilidad ("dónde se usa" = its legs). Gaps pre-loaded; legs from
// GET /articlesTrace/supplier/:id/trace.
const useStyles = makeStyles((theme) => ({
  drawer: { width: "50vw", minWidth: 620, maxWidth: 940 },
  tabs: { display: "flex", gap: 8, padding: "0 16px 8px" },
  body: { padding: theme.spacing(0, 2, 3) },
  sub: { fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.palette.text.secondary, margin: "6px 0" },
  empty: { color: "#94a3b8", padding: theme.spacing(1, 0) },
}));
const muted = { color: "#94a3b8" };

const SupplierTracePanel = ({ api, supplierId, supplierName, gaps = [], contracted, initialTab = "pendientes", onClose, onChanged }) => {
  const classes = useStyles();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab);
  const { rows, busyId, provide, assume, reject } = useGapResolution(api, gaps, supplierId, onChanged);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await api("GET", `/articlesTrace/supplier/${supplierId}/trace`);
      if (!alive) return;
      setD(r.ok ? r.data : null);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [api, supplierId]);

  const sortedGaps = sortPendingFirst(rows);
  const pendingCount = pendingCountOf(rows);
  const legs = d?.legs ?? [];

  return (
    <Drawer anchor="right" open onClose={onClose} classes={{ paper: classes.drawer }}>
      <Box p={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" style={{ lineHeight: 1.2 }}>{d?.name || supplierName || "Proveedor"}</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className={classes.tabs}>
        <Button size="small" variant={tab === "pendientes" ? "contained" : "outlined"} color="primary" onClick={() => setTab("pendientes")}>
          Pendientes{pendingCount ? ` (${pendingCount})` : ""}
        </Button>
        <Button size="small" variant={tab === "trace" ? "contained" : "outlined"} color="primary" onClick={() => setTab("trace")}>
          Trazabilidad
        </Button>
      </Box>

      <Box className={classes.body}>
        {tab === "pendientes" &&
          (sortedGaps.length === 0 ? (
            <div className={classes.empty}>Sin pendientes en este proveedor.</div>
          ) : (
            sortedGaps.map((g) => (
              <GapActionRow
                key={g.id}
                gap={g}
                contracted={contracted}
                busy={busyId === g.id}
                onProvide={provide}
                onAssume={assume}
                onReject={reject}
              />
            ))
          ))}

        {tab === "trace" && (
          <>
            {loading && (
              <Box textAlign="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading && !d && <Box style={muted} mt={2}>Sin trazabilidad</Box>}
            {!loading && d && (
              <>
                <div className={classes.sub}>Dónde se usa ({legs.length})</div>
                {legs.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Rol</TableCell>
                        <TableCell>Material / Artículo</TableCell>
                        <TableCell>Proceso</TableCell>
                        <TableCell>Origen</TableCell>
                        <TableCell>Transporte</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {legs.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell style={{ fontSize: 12 }}>{l.role}</TableCell>
                          <TableCell style={{ fontSize: 12 }}>{l.entity ?? "—"}</TableCell>
                          <TableCell style={{ fontSize: 12 }}>{l.process ?? "—"}</TableCell>
                          <TableCell style={{ fontSize: 12 }}>{l.origin ?? "—"}</TableCell>
                          <TableCell style={{ fontSize: 12 }}>{l.transport ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <span style={muted}>No se usa en ninguna traza</span>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default SupplierTracePanel;
